﻿import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import Swiper from 'swiper';

import 'swiper/swiper.min.css';
import 'swiper/modules/thumbs.min.css';
import { Thumbs } from 'swiper/modules';

interface FullscreenEventDetail {
    isOpen: boolean;
}

const FULLSCREEN_EVENT_NAME = 'tk-asset-gallery-fullscreen-update';

export default class TKAssetGallery extends TKCustomElementFactory {
    previewElement?: HTMLElement;
    thumbnailsElement?: HTMLElement;
    previewSwiper?: Swiper;
    thumbnailsSwiper?: Swiper;
    iframeTemplate?: HTMLTemplateElement;
    fullscreenCloseButton?: HTMLButtonElement;
    isOpen: boolean = false;
    maxThumbnails: number | 'auto' = 'auto';

    constructor() {
        super();
        this.initializeElements();
    }

    connectedCallback(): void {
        this.validateRequiredElements();
        this.initSwipers();
        this.setupLightbox();
        this.handleVideoSlides();
    }

    protected initializeElements(): void {
        this.previewElement = this.querySelector('[data-tk-asset-preview]') || undefined;
        this.thumbnailsElement = this.querySelector('[data-tk-asset-thumbnails]') || undefined;
        this.iframeTemplate = this.querySelector('[data-tk-iframe-template]') || undefined;
        this.fullscreenCloseButton = this.querySelector('[data-tk-close-fullscreen]') || undefined;
    }

    protected validateRequiredElements(): void {
        if (!this.previewElement || !this.thumbnailsElement) {
            throw new Error('Asset Gallery: Missing required elements for preview or thumbnails!');
        }
    }

    protected initSwipers(): void {
        if (!this.previewElement || !this.thumbnailsElement) return;

        this.initThumbnailsSwiper();
        this.initPreviewSwiper();
        this.setupFullscreenEventListener();
    }

    protected initThumbnailsSwiper(): void {
        if (!this.thumbnailsElement) return;
        this.thumbnailsSwiper = new Swiper(this.thumbnailsElement, {
            modules: [Thumbs],
            spaceBetween: 16,
            slidesPerView: 'auto',
            freeMode: true,
            watchSlidesProgress: true,
        });
    }

    protected initPreviewSwiper(): void {
        if (!this.previewElement) return;
        this.previewSwiper = new Swiper(this.previewElement, {
            init: false,
            modules: [Thumbs],
            spaceBetween: 16,
            thumbs: {
                swiper: this.thumbnailsSwiper,
                slideThumbActiveClass: 'tk-asset-gallery-item--selected',
            },
        });

        this.previewSwiper.on('afterInit', this.setupThumbnailOverflow.bind(this));
        this.previewSwiper.on('slideChange', this.stopAllClips.bind(this));
        this.previewSwiper.init();
    }

    protected setupFullscreenEventListener(): void {
        this.pushListener({
            event: FULLSCREEN_EVENT_NAME,
            element: this,
            action: (event: CustomEvent<FullscreenEventDetail>) => {
                this.toggleThumbnailAlignment(event.detail.isOpen);
            },
        });
    }

    protected toggleThumbnailAlignment(isOpen: boolean): void {
        if (!this.thumbnailsSwiper) return;
        this.thumbnailsSwiper.wrapperEl.classList.toggle('flex--justify-center', isOpen);
    }

    protected setupLightbox(): void {
        if (!this.previewElement) return;

        this.pushListener({
            event: 'click',
            element: this.previewElement,
            action: this.openLightbox.bind(this),
        });
    }

    protected openLightbox(): void {
        if (this.isOpen || !this.fullscreenCloseButton) return;

        this.toggleFullscreen(true);
        this.setupCloseButtonListener();
        this.dispatchFullscreenEvent(true);
    }

    protected closeLightbox(): void {
        this.toggleFullscreen(false);
        this.dispatchFullscreenEvent(false);
    }

    protected toggleFullscreen(isOpen: boolean): void {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        this.classList.toggle('tk-asset-gallery--fullscreen', isOpen);
        if (this.fullscreenCloseButton) {
            this.fullscreenCloseButton.hidden = !isOpen;
        }
        this.isOpen = isOpen;
    }

    protected setupCloseButtonListener(): void {
        this.pushListener({
            event: 'click',
            element: this.fullscreenCloseButton!,
            action: this.closeLightbox.bind(this),
        });
    }

    protected dispatchFullscreenEvent(isOpen: boolean): void {
        this.dispatchEvent(
            new CustomEvent(FULLSCREEN_EVENT_NAME, {
                detail: { isOpen },
            }),
        );
    }

    protected setupThumbnailOverflow(): void {
        if (!this.previewSwiper) return;

        const thumbnails = this.previewSwiper.thumbs.swiper.slides;

        if (this.maxThumbnails === 'auto') {
            this.setupAutoThumbnailOverflow(thumbnails);
        } else if (typeof this.maxThumbnails === 'number') {
            this.setupFixedThumbnailOverflow(thumbnails);
        }
    }

    protected setupAutoThumbnailOverflow(thumbnails: HTMLElement[]): void {
        const container = this.thumbnailsElement;
        if (!container) return;

        const resizeObserver = new ResizeObserver(() => {
            this.handleAutoThumbnailResize(container, thumbnails);
        });

        resizeObserver.observe(container);
    }

    protected handleAutoThumbnailResize(container: HTMLElement, thumbnails: HTMLElement[]): void {
        const availableWidth = container.clientWidth;
        let usedWidth = 0;
        let visibleCount = 0;
        const gap = Number(this.thumbnailsSwiper?.originalParams.spaceBetween);

        this.resetThumbnailVisibility(thumbnails);

        thumbnails.forEach((element) => {
            const itemWidth = (element as HTMLElement).offsetWidth;
            const tileWithGap = itemWidth + gap;
            if (usedWidth + (tileWithGap * 2 - gap) <= availableWidth) {
                usedWidth += tileWithGap;
                element.hidden = false;
                visibleCount++;
            } else {
                element.hidden = true;
            }
        });

        const hiddenCount = thumbnails.length - visibleCount;
        this.renderMoreCounter(hiddenCount);
    }

    protected resetThumbnailVisibility(thumbnails: HTMLElement[]): void {
        const existingCounter = this.thumbnailsElement?.querySelector('.tk-asset-gallery-item--more');
        if (existingCounter) existingCounter.remove();
        thumbnails.forEach((element) => (element.hidden = false));
    }

    protected setupFixedThumbnailOverflow(thumbnails: HTMLElement[]): void {
        if (typeof this.maxThumbnails !== 'number') return;
        const maxThumbnails = this.maxThumbnails;
        thumbnails.forEach((element, index) => {
            element.hidden = index >= maxThumbnails;
        });

        const hiddenCount = thumbnails.length - this.maxThumbnails!;
        this.renderMoreCounter(hiddenCount);
    }

    protected renderMoreCounter(hiddenCount: number): void {
        if (hiddenCount <= 1) return;

        const counter = document.createElement('div');
        counter.classList.add('tk-asset-gallery-item', 'tk-asset-gallery-item--more');
        counter.setAttribute('data-count', `+${hiddenCount}`);
        this.thumbnailsSwiper?.wrapperEl.appendChild(counter);

        this.pushListener({
            event: 'click',
            element: counter,
            action: this.openLightbox.bind(this),
        });

        this.thumbnailsSwiper?.update();
    }

    protected handleVideoSlides(): void {
        this.previewElement?.querySelectorAll<HTMLElement>('[data-tk-video-url]').forEach((slide) => {
            slide.addEventListener('click', () => this.setupVideoSlide(slide));
        });
    }

    protected setupVideoSlide(slide: HTMLElement): void {
        const url = new URL(slide.dataset.tkVideoUrl || '');
        url.searchParams.set('enablejsapi', '1');
        if (!this.iframeTemplate) return;

        const iframe = this.iframeTemplate.content.firstElementChild?.cloneNode(true) as HTMLIFrameElement;
        iframe.src = url.href;
        slide.innerHTML = '';
        slide.appendChild(iframe);
    }

    protected stopAllClips(): void {
        const iframes = this.querySelectorAll('iframe');
        iframes.forEach((iframe) => {
            iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), '*');
        });
    }
}
